import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import { API_CONSTANTS } from '../../utils/constants';

import type { RootState } from '../../store';
import type { AdminIdentity } from '../../model/adminIdentity';

export interface ProfileState {
  details: AdminIdentity | null;
  loading: boolean;
  error: any;
}

const initialState: ProfileState = {
  details: null,
  loading: false,
  error: null,
};

const normalizeRoleCode = (code?: string) =>
  code ? String(code).trim().toUpperCase() : '';

const normalizeProfileDetails = (details: any) => {
  if (!details) return details;
  const primaryRoleCode = normalizeRoleCode(details.groupCode || details.role);
  const normalizedGroups = Array.isArray(details.groups)
    ? details.groups.map((group: any) => ({
        ...group,
        groupCode: normalizeRoleCode(group.groupCode),
      }))
    : details.groups;

  return {
    ...details,
    groupCode: primaryRoleCode,
    role: primaryRoleCode,
    groups: normalizedGroups,
  };
};

export const fetchUserDetails = createAsyncThunk(
  'user/fetchUserDetails',
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        return rejectWithValue('Unauthorized');
      }
      const response = await fetch(
        API_CONSTANTS.BASE_URL + API_CONSTANTS.IDENTITY_ENDPOINT,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.status === 498) {
        return rejectWithValue('Unauthorized');
      }
      if (response.status === 401) {
        return rejectWithValue('Unauthorized');
      }
      if (response.status === 403) {
        return rejectWithValue('Unauthorized');
      }
      if (response.status === 429) {
        return rejectWithValue('Rate limited');
      }
      if (!response.ok) {
        throw new Error('Failed to fetch user');
      }

      return await response.json();
    } catch (error: unknown) {
      if (error instanceof Error) {
        return rejectWithValue(error.message);
      }
      return rejectWithValue('An unknown error occurred');
    }
  },
  {
    condition: (_, { getState }) => {
      const token = localStorage.getItem('access_token');
      if (!token) {
        return false;
      }
      const state = getState() as RootState;
      if (state.profile.loading) {
        return false;
      }
      return true;
    },
  }
);

const profileSlice = createSlice({
  name: 'profile',
  initialState,
  reducers: {
    logout: (state) => {
      state.details = null;
      state.loading = false;
      state.error = null;
      localStorage.removeItem('access_token');
    },
    updateUserLocally: (state, action) => {
      state.details = state.details
        ? normalizeProfileDetails({ ...state.details, ...action.payload })
        : null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserDetails.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUserDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.details = normalizeProfileDetails(action.payload.data);
      })
      .addCase(fetchUserDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? 'Failed to load user';
      });
  },
});

export const selectUser = (state: RootState) => state.profile.details;
export const selectUserPermissions = (state: RootState) =>
  state.profile.details?.permissions;

export const { logout, updateUserLocally } = profileSlice.actions;

export default profileSlice;
